import type {
  WidgetButtonShape,
  WidgetButtonSize,
  WidgetPosition,
  WidgetTemplateStyle,
} from '@/hooks/api/useWidgets'

// ============================================================================
// Form state
// ============================================================================

/** Colours are held as typed hex text so a half-typed value never resets. */
export interface WidgetStyleFormState {
  shape: WidgetButtonShape
  size: WidgetButtonSize
  iconOnly: boolean
  position: WidgetPosition
  offsetX: string
  offsetY: string
  buttonColor: string
  iconColor: string
  panelColor: string
  actionColor: string
}

/** The brand palette the Colors "Reset" returns to. */
export const WIDGET_BRAND_COLORS = {
  buttonColor: '#36D26A',
  iconColor: '#FFFFFF',
  panelColor: '#FFFFFF',
  actionColor: '#222222',
} as const

/** The launcher placement the Placement "Reset" returns to. */
export const WIDGET_PLACEMENT_DEFAULTS = {
  position: 'bottom-left',
  offsetX: '24',
  offsetY: '24',
} as const satisfies Pick<WidgetStyleFormState, 'position' | 'offsetX' | 'offsetY'>

export const WIDGET_BUTTON_SHAPES: readonly WidgetButtonShape[] = ['circle', 'square']
export const WIDGET_BUTTON_SIZES: readonly WidgetButtonSize[] = ['S', 'M', 'L']

/** Only the bottom corners exist — the launcher is anchored to the page floor. */
export const WIDGET_CORNERS: readonly WidgetPosition[] = ['bottom-left', 'bottom-right']

/** Launcher diameter per size, as the preview renders it. */
export const WIDGET_BUTTON_PIXELS: Record<WidgetButtonSize, number> = { S: 40, M: 48, L: 56 }

/** Offsets are pixels on the host page; past this the launcher leaves the fold. */
export const WIDGET_OFFSET_MAX = 400

export function createDefaultStyleFormState(): WidgetStyleFormState {
  return {
    shape: 'circle',
    size: 'S',
    iconOnly: false,
    ...WIDGET_PLACEMENT_DEFAULTS,
    ...WIDGET_BRAND_COLORS,
  }
}

export function fromTemplateStyle(style: WidgetTemplateStyle): WidgetStyleFormState {
  const defaults = createDefaultStyleFormState()

  return {
    shape: style.shape ?? defaults.shape,
    size: style.size ?? defaults.size,
    iconOnly: style.icon_only ?? defaults.iconOnly,
    position: style.position,
    offsetX: String(style.offset_x),
    offsetY: String(style.offset_y),
    buttonColor: style.colors.whatsapp_button ?? defaults.buttonColor,
    iconColor: style.colors.whatsapp_icon ?? defaults.iconColor,
    panelColor: style.colors.panel_background,
    actionColor: style.colors.action_buttons,
  }
}

function parseOffsetNumber(value: string): number {
  const parsed = Number.parseInt(value, 10)

  if (!Number.isFinite(parsed)) {
    return Number(WIDGET_PLACEMENT_DEFAULTS.offsetX)
  }

  return Math.min(Math.max(parsed, 0), WIDGET_OFFSET_MAX)
}

export function toTemplateStyle(state: WidgetStyleFormState): WidgetTemplateStyle {
  return {
    launcher_mode: 'floating',
    position: state.position,
    offset_x: parseOffsetNumber(state.offsetX),
    offset_y: parseOffsetNumber(state.offsetY),
    shape: state.shape,
    size: state.size,
    icon_only: state.iconOnly,
    colors: {
      action_buttons: state.actionColor,
      panel_background: state.panelColor,
      whatsapp_button: state.buttonColor,
      whatsapp_icon: state.iconColor,
    },
  }
}

// ============================================================================
// Colour helpers
// ============================================================================

const HEX_COLOR = /^#[0-9a-f]{6}$/i

export function isValidHexColor(value: string): boolean {
  return HEX_COLOR.test(value.trim())
}

function parseHexRgb(value: string): { r: number; g: number; b: number } | null {
  const hex = value.trim().replace('#', '')

  if (!/^[0-9a-f]{6}$/i.test(hex)) {
    return null
  }

  return {
    r: Number.parseInt(hex.slice(0, 2), 16),
    g: Number.parseInt(hex.slice(2, 4), 16),
    b: Number.parseInt(hex.slice(4, 6), 16),
  }
}

function srgbChannelLuminance(channel: number): number {
  const value = channel / 255
  return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
}

/**
 * WCAG relative luminance, or null when the value is not a complete hex colour.
 * The preview uses this to pick ink that still reads on a custom panel or CTA.
 */
export function relativeLuminance(value: string): number | null {
  const rgb = parseHexRgb(value)
  if (!rgb) return null

  return (
    0.2126 * srgbChannelLuminance(rgb.r) +
    0.7152 * srgbChannelLuminance(rgb.g) +
    0.114 * srgbChannelLuminance(rgb.b)
  )
}

/**
 * Midpoint where white and black have equal contrast against the surface
 * ((L + 0.05)² = 0.0525 → L ≈ 0.179). Above it, dark ink wins.
 */
const DARK_INK_LUMINANCE = 0.179

const PANEL_INK_DARK = {
  primary: '#1c1917',
  secondary: '#57534e',
  muted: '#78716c',
} as const

const PANEL_INK_LIGHT = {
  primary: '#fafaf9',
  secondary: '#d6d3d1',
  muted: '#a8a29e',
} as const

export type WidgetPanelInk = {
  primary: string
  secondary: string
  muted: string
}

/** True when the surface is dark enough that light ink is the readable choice. */
export function isDarkSurface(background: string): boolean {
  const luminance = relativeLuminance(background)
  return luminance !== null && luminance <= DARK_INK_LUMINANCE
}

/**
 * Title / body / muted colours for text sitting on `background`. Incomplete hex
 * (the user is still typing) keeps the light-panel palette.
 */
export function widgetPanelInk(background: string): WidgetPanelInk {
  return isDarkSurface(background) ? PANEL_INK_LIGHT : PANEL_INK_DARK
}

/** Solid label colour on a CTA or glyph — white on dark, stone on light. */
export function contrastingFill(background: string): string {
  return isDarkSurface(background) ? '#ffffff' : PANEL_INK_DARK.primary
}

/**
 * Glyph colour on the launcher. Keeps the chosen icon when it already reads;
 * flips to white on a dark button (or stone on a near-white one) when it does
 * not. Incomplete hex falls back to a contrasting fill.
 */
export function widgetIconFill(iconColor: string, buttonColor: string): string {
  if (!isValidHexColor(iconColor)) {
    return contrastingFill(buttonColor)
  }

  const icon = iconColor.trim()

  if (isDarkSurface(buttonColor) && isDarkSurface(icon)) {
    return '#ffffff'
  }

  if (isLightColor(buttonColor) && isLightColor(icon)) {
    return PANEL_INK_DARK.primary
  }

  return icon
}

/**
 * Near-white swatches need an outline of their own, or they disappear into the
 * field they sit in. Uses perceived luminance so a pale yellow counts as light.
 */
export function isLightColor(value: string): boolean {
  const rgb = parseHexRgb(value)
  if (!rgb) return false

  return 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b > 225
}
